import { ArrowRight, ListChecks, Buildings, UserCircle, Users, ClipboardText, CalendarBlank } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { PRIORITY_BADGE, PROJECT_STATUS_BADGE, projectBadgeKey, fmtDate, dispText } from '../plannerConstants';

const ROLE_BADGE = {
  owner: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  member: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  task_member: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};
const ROLE_ICON = { owner: UserCircle, member: Users, task_member: ClipboardText };

// U9 — プロジェクトカードは1つの部品に統一（部署画面 / マイプロジェクト）。
// 体裁がずれていたので、名前・バッジ・説明・タスク数・期間・部署・担当の順で固定。
export default function ProjectCard({ project: p, onOpen, testid, statusTestId, deptTestId, roleTestId, role, showDept = false }) {
  const { t } = useT();
  const RoleIcon = role ? ROLE_ICON[role] : null;
  return (
    <button
      onClick={onOpen}
      data-testid={testid}
      className="group h-full flex flex-col text-left bg-white rounded-xl border border-zinc-200 p-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 hover:border-indigo-200 transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-zinc-900 leading-snug flex-1 line-clamp-2"><span data-no-translate>{dispText(p.name)}</span></h3>
        <div className="flex gap-1 shrink-0">
          <Badge variant="outline" className={`${PRIORITY_BADGE[p.priority]} text-[10px] px-1.5 py-0`}>
            {t(`planner.priority.${p.priority}`)}
          </Badge>
          <Badge variant="outline" data-testid={statusTestId}
            className={`${PROJECT_STATUS_BADGE[projectBadgeKey(p)]} text-[10px] px-1.5 py-0`}>
            {t(`planner.projectStatus.${projectBadgeKey(p)}`)}
          </Badge>
        </div>
      </div>
      <p className="text-xs text-zinc-500 line-clamp-2 mb-2 min-h-[2rem]">{dispText(p.description) || ''}</p>
      <div className="mt-auto space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-zinc-500">
          <span className="inline-flex items-center gap-1">
            <ListChecks size={13} weight="duotone" /> {t('planner.gantt.tasks', { done: p.done_count, total: p.task_count })}
          </span>
          <span className="inline-flex items-center gap-1 text-zinc-400 tabular-nums">
            <CalendarBlank size={12} weight="duotone" /> {fmtDate(p.start_date)} – {fmtDate(p.end_date)}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px] text-zinc-500">
          {showDept && p.department_name && (
            <span className="inline-flex items-center gap-1 text-zinc-400" data-testid={deptTestId}>
              <Buildings size={12} weight="duotone" /> {p.department_name}
            </span>
          )}
          {p.owner_name && <span className="truncate text-zinc-400"><span data-no-translate>{p.owner_name}</span></span>}
          {role && (
            <Badge variant="outline" className={`${ROLE_BADGE[role]} text-[10px] px-1.5 py-0 ml-auto`}
              data-testid={roleTestId}>
              <RoleIcon size={10} weight="fill" className="mr-0.5 inline" />
              {t(`planner.myProjects.role.${role}`)}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs font-medium text-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity">
          {t('planner.dept.open')} <ArrowRight size={12} weight="bold" />
        </div>
      </div>
    </button>
  );
}
